import { useMutation } from '@tanstack/react-query';
import {
  QuoteDTO,
  deleteQuoteV2SectionMutation
} from '@setvi/shared/services/react-query';
import { removeSectionKeepItemsOptimistically } from '../utils/quote-helpers';
import { useQuoteCache } from './use-quote-cache';

/**
 * Remove section from quote but keep its items at the root level.
 * Passes keepItems=true to the API so the backend moves children
 * to root before deleting the section.
 */
export const useRemoveSectionKeepItems = (quoteId: string) => {
  const cache = useQuoteCache(quoteId);
  const { mutationFn: deleteSection } = deleteQuoteV2SectionMutation();

  return useMutation({
    mutationFn: (sectionId: string) =>
      deleteSection({ quoteId, sectionId, keepItems: true }),

    onMutate: async (sectionId: string) => {
      await cache.cancel();

      const previousQuote = cache.getQuote();
      const previousItems = cache.getItems();

      const composed = previousQuote
        ? ({
            ...previousQuote,
            items: previousItems || []
          } as unknown as QuoteDTO)
        : undefined;

      const updated = removeSectionKeepItemsOptimistically(composed, sectionId);

      if (updated) {
        const { items: updatedItems, ...quoteData } = updated as any;
        cache.setQuote(() => quoteData);
        cache.setItems(() => updatedItems);
      }

      return { previousQuote, previousItems };
    },

    onError: (_error, _variables, context) => {
      if (context?.previousQuote) {
        cache.setQuote(() => context.previousQuote);
      }
      if (context?.previousItems) {
        cache.setItems(() => context.previousItems);
      }
    },

    onSuccess: () => {
      cache.invalidate();
    }
  });
};
